import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { useRouteScrollRestoration } from "../state/routeState";

/**
 * Route-change side effects (design.md §3 point 5 + §7 `app/` module list):
 * mounted once inside `AppShell.tsx` alongside `<Outlet>`. Renders nothing.
 *
 * 1. Moves focus to the destination screen's `<h1>` — but only when the
 *    PATHNAME changes, never for a same-route search-param-only update
 *    (e.g. typing in a filter input that calls `navigate(..., {replace})`).
 *    Otherwise every keystroke in a filter would steal focus away from the
 *    input the user is actively typing in.
 * 2. Delegates scroll position save/restore to `useRouteScrollRestoration`,
 *    keyed by `pathname+search` per design.md §3 point 3.
 */
export function RouteFocus({ mainId = "main-content" }: { mainId?: string } = {}) {
  const location = useLocation();
  const navigationType = useNavigationType();

  useRouteScrollRestoration(`${location.pathname}${location.search}`, navigationType, mainId);

  useEffect(() => {
    const container = document.getElementById(mainId);
    const heading = container?.querySelector<HTMLElement>("h1");
    if (!heading) {
      return;
    }

    const hadTabIndex = heading.hasAttribute("tabindex");
    if (!hadTabIndex) {
      heading.setAttribute("tabindex", "-1");
    }
    heading.focus();
    // Only `location.pathname` (not `location.search`) retriggers this
    // effect by design — a same-route search update must not steal focus
    // from an active control (design.md §3 point 5).
  }, [location.pathname, mainId]);

  return null;
}
